import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useOktaAuth } from "@okta/okta-react";
import { RootState } from "../../store/store";
import {
  resetForecastState,
  setForecastFilter,
  setRcaFilter,
  setResultVisualizationFilter,
} from "../../store/filterSlice";
import "./ForecastAnalysisFilter.css";
import {
  useForecastClusterFilter,
  useShortTermData,
  useMidTermData,
} from "../../api/filtersApi";
import {
  ChartData,
  foreCastBackTestingApiDataLoading,
  foreCastBackTestingAverageDataSetter,
  foreCastBackTestingChartDataSetter,
  foreCastBackTestingTableDataSetter,
  resetForecastBackTestingApiDataLoading,
  resetForecastBackTestingAverageState,
  resetForecastBackTestingChartState,
  resetForecastBackTestingTableState,
  updateBackTestingTotalPages,
} from "../../store/foreCastBackTestingDataSlice";
import {
  resetForecastDecomState,
  setForecastData,
  updateBackDecompositionTotalPages,
} from "../../store/foreCastDecomSlice";
import {
  resetForecastData,
  setChartData,
  setDynamicTableHeader,
  setHolidayIndicators,
  setPageLoadingState,
  setTableRowData,
  updateForecastAnalysisTotalPages,
} from "../../store/foreCastDataSlice";
import NotificationBar from "../../baseComponent/NotificationBar/NotificationBar";
import {
  calculateTotalPages,
  forecastAnalysisChartApi,
  forecastAnalysisDefaultGroupBy,
  forecastAnalysisHolidaysApi,
  forecastAnalysisTableApi,
  forecastBackTestingAverageApi,
  forecastBackTestingChartApi,
  forecastBackTestingDefaultGroupBy,
  forecastBackTestingTableApi,
  forecastDecompositionApi,
  mapperForGroupByLableToKey,
} from "../../api/forecastPageApi";
import { PAGE_LIMIT } from "../../utils/constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getResponseDate } from "../../utils/functions";
import FilterDropdown from "../../baseComponent/Filter/FilterDropdown";
import FilterButtons from "../../baseComponent/Filter/FilterButtons";
import FilterResetModal from "../../baseComponent/Filter/FilterResetModal";
import { DynamicMappings } from "../../utils/dynamicMappings";

interface ForecastAnalysisFilterType {
  pageLoadingHandler: (data: boolean) => void;
}

const ForecastAnalysisFilter: React.FC<ForecastAnalysisFilterType> = ({
  pageLoadingHandler,
}) => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [showNotification, setHideNotification] = useState<boolean>(false);
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const dataShowKeyValueMappings = DynamicMappings();
  const filter = useSelector((state: RootState) => state.forecastFilter);
  const userData = useSelector((state: RootState) => state.user);

  const { data: shortTermApiData } = useShortTermData(
    authState?.accessToken?.accessToken!,
    "short",
    userData?.currentSector ? userData.currentSector : {}
  );
  const { data: midTermApiData } = useMidTermData(
    authState?.accessToken?.accessToken!,
    "mid",
    userData?.currentSector ? userData.currentSector : {}
  );
  const { data: clusterApiData } = useForecastClusterFilter(
    authState?.accessToken?.accessToken!,
    "cluster",
    userData?.currentSector ? userData.currentSector : {}
  );

  const applyFilters = async () => {
    pageLoadingHandler(true);
    dispatch(
      foreCastBackTestingApiDataLoading({
        isDataLoading: true,
      })
    );

    makeForecastAnalysisApiCall(payloadFilters, authState);
    makeBackTesingApiCall(
      {
        snapshotDate: getResponseDate(
          filter?.term?.toString() === "ST"
            ? shortTermApiData?.find(
                (item: any) => item?.module === "ResultVisualization"
              )?.snapshotDates
            : midTermApiData?.find(
                (item: any) => item?.module === "ResultVisualization"
              )?.snapshotDates,
          filter?.term?.toString() === "ST"
            ? filter?.shortSnapshotDate
            : filter?.midSnapshotDate
        ),
        backtestingPayloadFilters,
      },
      authState
    );
    makeForecastAnalsisDecompostionApiCall(payloadFilters, authState);
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("invoke") === "true") {
      applyFilters();

      setTimeout(() => {
        navigate("/statisticalforecastanalysis", { replace: true });
      }, 1000);
    }
  }, [searchParams, navigate]);

  const backTestingPagination = useSelector(
    (state: RootState) => state.foreCastBackTestingPagination
  );
  const forecastAnalysisPaginationData = useSelector(
    (state: RootState) => state.forecastAnalysisPaginationData
  );
  const foreCastBackTestingTableData = useSelector(
    (state: RootState) => state.foreCastBackTestingTableDataSlice
  );

  const foreCastDecompositionPagination = useSelector(
    (state: RootState) => state.foreCastDecompositionPaginationData
  );

  const lagDropdownData = useSelector(
    (state: RootState) => state.lagDropdownData
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    dispatch(resetForecastData());
    dispatch(resetForecastDecomState());
    dispatch(resetForecastBackTestingTableState());
    dispatch(resetForecastBackTestingChartState());
    dispatch(resetForecastBackTestingAverageState());
    dispatch(resetForecastBackTestingApiDataLoading());
  }, []);

  const decompositionPayload = {
    shortTermMidTermCode: filter?.term.toString(),
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packTypeName: filter.ppg,
    keyAccount: filter?.keyAccount,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesRegionName: filter?.salesRegion,
    salesCountryCode: filter?.salesCountry,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    packageTypeName: filter?.container,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    eachUPC: filter?.eachUPC,
    salesZoneName: filter?.zone,
  };

  const payloadFilters = {
    ...decompositionPayload,
    pageNumber: 1,
    // pageSize: PAGE_LIMIT,
    // segment
    // Ac1/AC2
    // recipe runs
    // country
    // IBPDemandGroup
    // PPG
    // planningItem
  };

  const backtestingPayloadFilters: any = {
    shortTermMidTermCode: filter?.term.toString(),
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    keyAccount: filter?.keyAccount,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    eachUPC: filter?.eachUPC,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageTypeName: filter?.container,
    salesRegionName: filter?.salesRegion,
    salesZoneName: filter?.zone,
    salesCountryCode: filter?.salesCountry,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    packageName: filter?.count,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    pageNumber: 1,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    pageSize: PAGE_LIMIT,
    // LATAM key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
  };

  if (
    userData?.sections?.forecastAnalysis?.table?.lagDropdown &&
    filter?.term?.toString() === "ST" &&
    lagDropdownData.foreCastAnalysisLagForSt !== null
  ) {
    backtestingPayloadFilters["forecastLagNumber"] = Number(
      lagDropdownData.foreCastAnalysisLagForSt
    );
  }
  if (
    userData?.sections?.forecastAnalysis?.table?.lagDropdown &&
    filter?.term?.toString() === "MT" &&
    lagDropdownData.foreCastAnalysisLagForMt !== null
  ) {
    backtestingPayloadFilters["forecastLagNumber"] = Number(
      lagDropdownData.foreCastAnalysisLagForMt
    );
  }

  const resetFilter = () => {
    dispatch(
      resetForecastState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetForecastData());
    dispatch(resetForecastDecomState());
    dispatch(resetForecastBackTestingTableState());
    dispatch(resetForecastBackTestingChartState());
    dispatch(resetForecastBackTestingAverageState());
    dispatch(resetForecastBackTestingApiDataLoading());

    // Reset the term to 'ST' in all relevant filters
    dispatch(setForecastFilter({ key: "term", value: ["ST"] }));
    dispatch(setRcaFilter({ key: "term", value: ["ST"] }));
    dispatch(setResultVisualizationFilter({ key: "term", value: ["ST"] }));

    handleClose();
    if (userData?.currentSector?.sector === "PFNA") {
      dispatch(
        setRcaFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
      dispatch(
        setForecastFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
      dispatch(
        setResultVisualizationFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
    }
  };

  useEffect(() => {
    if (
      backTestingPagination?.totalPages > 1 &&
      backTestingPagination?.isDataShownClicked === false
    ) {
      // restrict keys to be passed to API based on AC1/AC2 selection in PFNA
      const groupByPFNAData =
        userData?.currentSector?.sector === "PFNA" &&
        filter?.ac1ac2?.[0] !== undefined &&
        filter?.ac1ac2?.[0] === "AC1"
          ? backTestingPagination?.groupBySelectedField?.filter(
              (item) => item !== "Planning Item" && item !== "UPC"
            )
          : backTestingPagination?.groupBySelectedField;

      const backTestingGroupByOptions = mapperForGroupByLableToKey(
        groupByPFNAData,
        dataShowKeyValueMappings
      );

      const forecastBackTestingApiRequestBody = {
        ...backtestingPayloadFilters,
        snapshotDate: getResponseDate(
          filter?.term?.toString() === "ST"
            ? shortTermApiData?.find(
                (item: any) => item?.module === "ResultVisualization"
              )?.snapshotDates
            : midTermApiData?.find(
                (item: any) => item?.module === "ResultVisualization"
              )?.snapshotDates,
          filter?.term?.toString() === "ST"
            ? filter?.shortSnapshotDate
            : filter?.midSnapshotDate
        ),
        pageNumber: backTestingPagination?.pageNumber,
        pageSize: backTestingPagination?.pageLimit,
        groupBy: backTestingGroupByOptions?.selectedGroupByOptions,
      };
      dispatch(
        foreCastBackTestingTableDataSetter({
          isTableDataLoading: true,
          backTestingTableData:
            foreCastBackTestingTableData?.backTestingTableData,
          tableHeader: foreCastBackTestingTableData?.tableHeader,
        })
      );
      forecastBackTestingTableApi(forecastBackTestingApiRequestBody, authState)
        .then((response) => {
          const totalBackTestingPages = calculateTotalPages(
            PAGE_LIMIT,
            response?.totalCount
          );

          dispatch(
            updateBackTestingTotalPages({
              totalPages: totalBackTestingPages,
              pageLimit: backTestingPagination.pageLimit,
              pageNumber: backTestingPagination.pageNumber,
              isDataShownClicked: false,
            })
          );
          dispatch(
            foreCastBackTestingTableDataSetter({
              tableHeader: response?.dynamicTableHeader,
              backTestingTableData: response?.dfuBasedGroupedData,
              isTableDataLoading: false,
            })
          );
        })
        .catch((error) => {
          console.log("forecastBackTestingTableApi error", error);
        });
    }
  }, [backTestingPagination?.pageNumber]);

  useEffect(() => {
    if (
      forecastAnalysisPaginationData?.totalPages > 1 &&
      forecastAnalysisPaginationData?.isDataShownClicked === false
    ) {
      dispatch(setPageLoadingState(true));

      const groupByPFNAData =
        userData?.currentSector?.sector === "PFNA" &&
        filter?.ac1ac2?.[0] !== undefined &&
        filter?.ac1ac2?.[0] === "AC1"
          ? forecastAnalysisPaginationData?.groupBySelectedField?.filter(
              (item) => item !== "Planning Item" && item !== "UPC"
            )
          : forecastAnalysisPaginationData?.groupBySelectedField;

      const forecastAnalysisGroupByOptions = mapperForGroupByLableToKey(
        groupByPFNAData,
        dataShowKeyValueMappings
      );

      const forecastAnalysisApiRequestBody = {
        ...payloadFilters,
        pageNumber: forecastAnalysisPaginationData?.pageNumber,
        pageSize: forecastAnalysisPaginationData?.pageLimit,
        groupBy: forecastAnalysisGroupByOptions?.selectedGroupByOptions,
      };
      forecastAnalysisTableApi(forecastAnalysisApiRequestBody, authState)
        .then((response) => {
          const totalBackTestingPages = calculateTotalPages(
            PAGE_LIMIT,
            response?.totalCount
          );
          dispatch(
            updateForecastAnalysisTotalPages({
              totalPages: totalBackTestingPages,
              pageLimit: forecastAnalysisPaginationData.pageLimit,
              pageNumber: forecastAnalysisPaginationData.pageNumber,
              isDataShownClicked: false,
            })
          );
          dispatch(setTableRowData(response?.dfuBasedGroupedData));
          dispatch(setDynamicTableHeader(response?.dynamicTableHeader));
          dispatch(setPageLoadingState(false));
        })
        .catch((error) => {
          console.log("forecastBackTestingTableApi error", error);
          dispatch(setPageLoadingState(false));
        });
    }
  }, [forecastAnalysisPaginationData?.pageNumber]);

  useEffect(() => {
    if (foreCastDecompositionPagination?.totalPages > 1) {
      makeForecastAnalsisDecompostionApiCall(decompositionPayload, authState);
    }
  }, [foreCastDecompositionPagination?.pageNumber]);

  const makeForecastAnalsisDecompostionApiCall = (
    payloadFilters: any,
    authState: any
  ) => {
    dispatch(
      setForecastData({
        loadingDecomposition: true,
        chartData: {
          range: {
            start: 0,
            end: 0,
          },
        },
        displayNames: {},
        data: [],
      })
    );
    const foreCastDecompositionRequestBody = {
      ...decompositionPayload,
      // pageNumber: foreCastDecompositionPagination?.pageNumber,
      // pageSize: ,
      groupBy: [],
    };
    forecastDecompositionApi(foreCastDecompositionRequestBody, authState)
      .then((response) => {
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateBackDecompositionTotalPages({
            totalPages: totalBackTestingPages,
            pageLimit: foreCastDecompositionPagination.pageLimit,
            pageNumber: foreCastDecompositionPagination.pageNumber,
            isDataShownClicked:
              foreCastDecompositionPagination?.isDataShownClicked,
          })
        );
        dispatch(
          setForecastData({
            loadingDecomposition: false,
            chartData: response?.chartData,
            displayNames: response?.displayNames,
            data: response?.data,
          })
        );
        pageLoadingHandler(false);
      })
      .catch((error) => {
        console.log("forecastBackTestingTableApi error", error);
        dispatch(
          setForecastData({
            loadingDecomposition: true,
            chartData: {
              range: {
                start: 0,
                end: 0,
              },
            },
            displayNames: {},
            data: [],
          })
        );
        pageLoadingHandler(false);
      });
  };

  const makeForecastAnalysisApiCall = (payloadFilters: any, authState: any) => {
    dispatch(setPageLoadingState(true));
    dispatch(setChartData([]));
    dispatch(setTableRowData([]));
    dispatch(setDynamicTableHeader([]));
    dispatch(setHolidayIndicators({}));

    const groupByPFNAData =
      userData?.currentSector?.sector === "PFNA" &&
      filter?.ac1ac2?.[0] !== undefined &&
      filter?.ac1ac2?.[0] === "AC1"
        ? userData?.sections?.forecastAnalysis?.table?.defaultDataShown?.forecastAnalysis.filter(
            (item) => item !== "Planning Item" && item !== "UPC"
          )
        : userData?.sections?.forecastAnalysis?.table?.defaultDataShown
            ?.forecastAnalysis;

    const forecastAnalysisGroupByOptions = mapperForGroupByLableToKey(
      groupByPFNAData,
      dataShowKeyValueMappings
    );

    const forecastAnalysisApiRequestBody = {
      ...payloadFilters,
      pageNumber: forecastAnalysisPaginationData?.pageNumber,
      pageSize: forecastAnalysisPaginationData?.pageLimit,
      groupBy: forecastAnalysisGroupByOptions?.selectedGroupByOptions,
    };

    forecastAnalysisTableApi(forecastAnalysisApiRequestBody, authState)
      .then((response) => {
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateForecastAnalysisTotalPages({
            totalPages: totalBackTestingPages,
            pageLimit: forecastAnalysisPaginationData.pageLimit,
            pageNumber: forecastAnalysisPaginationData.pageNumber,
            isDataShownClicked:
              forecastAnalysisPaginationData?.isDataShownClicked,
          })
        );
        dispatch(setTableRowData(response?.dfuBasedGroupedData));
        dispatch(setDynamicTableHeader(response?.dynamicTableHeader));
        dispatch(setPageLoadingState(false));
      })
      .catch((error) => {
        console.log("forecastBackTestingTableApi error", error);
        dispatch(setPageLoadingState(false));
      });
    forecastAnalysisChartApi(forecastAnalysisApiRequestBody, authState)
      .then((response) => {
        dispatch(setChartData(response?.chartData));
      })
      .catch((error) => {
        console.log("forecastBackTestingChartApi error", error);
      });

    forecastAnalysisHolidaysApi(
      {
        shortTermMidTermCode: filter?.term.toString(),
        snapshotDate:
          filter?.term.toString() === "ST"
            ? filter?.shortSnapshotDate
            : filter?.midSnapshotDate,
        sector: userData?.currentSector?.sector,
        market: userData?.currentSector?.market,
        brandName: filter?.brand,
        subBrandName: filter?.subBrand,
        keyAccount: filter?.keyAccount,
        materialGlobalTradeItemNumberDescription: filter?.planningItem,
        productLineName: filter?.productLine,
        productGroupName: filter?.productGroup,
        packageSizeName: filter?.size,
        productCategoryName: filter?.category,
        portfolioGroupName: filter?.mg4PortfolioGroup,
        packageTypeName: filter?.container,
        salesRegionName: filter?.salesRegion,
        salesCountryCode: filter?.salesCountry,
        forecastModuleCode: filter?.ac1ac2,
        forecastEngineSegmentationId: filter?.segment,
        packageName: filter?.count,
        customerGlobalName: filter?.global,
        customerSectorNumber: filter?.customerSector,
        customerRegionName: filter?.customerRegion,
        customerGroupName: filter?.customerGroup,
        customerBusinessUnitName: filter?.businessUnit,
        customerMarketUnitName: filter?.marketUnit,
        ibpDemandGroup: filter?.ibpDemandGroup,
        customerTradeSegmentDescription: filter?.cg1TrageSegment,
        salesSectorName: filter?.salesSector,
        salesOrganizationCode: filter?.businessOrg,
        salesDivisionName: filter?.division,
        forecastEngineClusterId: filter?.clusters,
        pageNumber: 1,
        pageSize: PAGE_LIMIT,
        // LATAM key
        flavorName: filter?.flavourNames,
        customerChannelName: filter?.channels,
        customerDemandGroup: filter?.demandGroups,
        customerSubChannelName: filter?.subChannels,
        customerRegionalCluster: filter?.regionalClusters,
        locationGlobalName: filter?.globalCode,
        locationSectorName: filter?.locationSector,
        locationCountryName: filter?.locationCountry,
        planningLocationId: filter?.planningLocationId,
        locationRegionName: filter?.regionCode,
        // PFNA key
        alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
        eachUPC: filter?.eachUPC,
        salesZoneName: filter?.zone,
      },
      authState
    )
      .then((response) => {
        dispatch(setHolidayIndicators(response?.holidayIndicators || {}));
      })
      .catch((error) => {
        console.log("forecastAnalysisHolidaysApi error", error);
      });
  };

  const makeBackTesingApiCall = (payloadFilters: any, authState: any) => {
    dispatch(
      foreCastBackTestingTableDataSetter({
        isTableDataLoading: true,
        backTestingTableData: [],
        tableHeader: [],
      })
    );
    dispatch(
      foreCastBackTestingChartDataSetter({
        backTestingChartLabels: [],
        chartData: [],
        isChartDataLoading: true,
      })
    );
    dispatch(
      foreCastBackTestingAverageDataSetter({
        overallAverage: {
          accuracy: 0,
          bias: 0,
          historicalShipments: 0,
          statisticalForecast: 0,
        },
        overallPercent: [],
        isAverageDataLoading: true,
      })
    );

    const groupByPFNAData =
      userData?.currentSector?.sector === "PFNA" &&
      filter?.ac1ac2?.[0] !== undefined &&
      filter?.ac1ac2?.[0] === "AC1"
        ? userData?.sections?.forecastAnalysis?.table?.defaultDataShown?.backTesting.filter(
            (item) => item !== "Planning Item" && item !== "UPC"
          )
        : userData?.sections?.forecastAnalysis?.table?.defaultDataShown
            ?.backTesting;
    const backTestingGroupByOptions = mapperForGroupByLableToKey(
      groupByPFNAData,
      dataShowKeyValueMappings
    );

    const forecastBackTestingApiRequestBody = {
      ...backtestingPayloadFilters,
      snapshotDate: getResponseDate(
        filter?.term?.toString() === "ST"
          ? shortTermApiData?.find(
              (item: any) => item?.module === "ResultVisualization"
            )?.snapshotDates
          : midTermApiData?.find(
              (item: any) => item?.module === "ResultVisualization"
            )?.snapshotDates,
        filter?.term?.toString() === "ST"
          ? filter?.shortSnapshotDate
          : filter?.midSnapshotDate
      ),
      pageNumber: backTestingPagination?.pageNumber,
      pageSize: backTestingPagination?.pageLimit,
      groupBy: backTestingGroupByOptions?.selectedGroupByOptions,
    };

    forecastBackTestingTableApi(forecastBackTestingApiRequestBody, authState)
      .then((response) => {
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateBackTestingTotalPages({
            totalPages: totalBackTestingPages,
            pageLimit: backTestingPagination.pageLimit,
            pageNumber: backTestingPagination.pageNumber,
            isDataShownClicked: backTestingPagination.isDataShownClicked,
          })
        );
        dispatch(
          foreCastBackTestingTableDataSetter({
            tableHeader: response?.dynamicTableHeader,
            backTestingTableData: response?.dfuBasedGroupedData,
            isTableDataLoading: false,
          })
        );
      })
      .catch((error) => {
        console.log("forecastBackTestingTableApi error", error);
      });
    forecastBackTestingChartApi(forecastBackTestingApiRequestBody, authState)
      .then((response) => {
        const backTestingChartLable = response?.chartData?.map(
          (data: ChartData) => data.periodWeekNumber
        );
        dispatch(
          foreCastBackTestingChartDataSetter({
            chartData: response?.chartData,
            backTestingChartLabels: backTestingChartLable,
            isChartDataLoading: false,
          })
        );
      })
      .catch((error) => {
        console.log("forecastBackTestingChartApi error", error);
      });
    forecastBackTestingAverageApi(forecastBackTestingApiRequestBody, authState)
      .then((response) => {
        dispatch(
          foreCastBackTestingAverageDataSetter({
            overallAverage: response?.overallAverage,
            overallPercent: response?.overallPercent,
            isAverageDataLoading: false,
          })
        );
      })
      .catch((error) => {
        console.log("forecastBackTestingAverageApi error", error);
      });
  };

  const handleNotificationCloseEvent = (hideNotification: boolean) => {
    setHideNotification(hideNotification);
  };

  return (
    <div className="p-5 bg-secondary-bg mt-4 rounded">
      {open && (
        <FilterResetModal
          open={open}
          resetFilter={resetFilter}
          handleClose={handleClose}
        />
      )}
      <FilterButtons handleOpen={handleOpen} applyFilters={applyFilters} />
      <NotificationBar
        text=" The filters you have applied do not have any data that can be shown,
        please try again with different filters"
        shouldNotificationVisible={showNotification}
        onCloseNotification={handleNotificationCloseEvent}
      />
      <FilterDropdown />
    </div>
  );
};

export default ForecastAnalysisFilter;
